import React, { useState, useEffect } from 'react';
import {
  Box,
  CardContent,
  Typography,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Rating,
  FormControl,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Pagination
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Favorite,
  FavoriteBorder,
  Person,
  Info,
  Schedule,
  FavoriteOutlined
} from '@mui/icons-material';
import CharacterAvatar from './CharacterAvatar';
import { getApiUrl } from '../config';
import { GlassCard, GlassTextField, GlassButton, GlassIconButton, GlassInfoCard, GlassBackground, GlassContainer } from './ui/GlassComponents';
import { GlassPageHeader } from './ui/GlassLayoutComponents';

const PlayerRelations = ({ authData, sessionData, onBack }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [relations, setRelations] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [biographyDialogOpen, setBiographyDialogOpen] = useState(false);
  const [updatingRelation, setUpdatingRelation] = useState(null);
  const [filterMode, setFilterMode] = useState('relationStatus');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlayers, setTotalPlayers] = useState(0);

  const selectedDisplayName = sessionData?.selectedDisplayName;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch players when page, search, or items per page changes
  useEffect(() => {
    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, itemsPerPage]);

  // Fetch relations once on mount
  useEffect(() => {
    fetchRelations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      const response = await fetch(getApiUrl(`/players?${params}`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Filter out the current player
        const otherPlayers = result.players.filter(player => player.displayName !== selectedDisplayName);
        setPlayers(otherPlayers);
        
        // Update pagination info
        if (result.pagination) {
          setTotalPages(result.pagination.totalPages);
          setTotalPlayers(result.pagination.total);
        }
      } else {
        setError('Failed to load players');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelations = async () => {
    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/relations`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setRelations(result.relations || {});
      }
    } catch (err) {
      console.error('Failed to fetch relations:', err);
    }
  };

  const setPlayerRelation = async (toDisplayName, rating) => {
    setUpdatingRelation(toDisplayName);
    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/relations/${toDisplayName}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`
        },
        body: JSON.stringify({ rating })
      });

      if (response.ok) {
        setRelations(prev => ({
          ...prev,
          [toDisplayName]: rating
        }));
      } else {
        console.error('Failed to set relation');
      }
    } catch (err) {
      console.error('Failed to set relation:', err);
    } finally {
      setUpdatingRelation(null);
    }
  };

  const removePlayerRelation = async (toDisplayName) => {
    setUpdatingRelation(toDisplayName);
    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/relations/${toDisplayName}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        }
      });

      if (response.ok) {
        setRelations(prev => {
          const newRelations = { ...prev };
          delete newRelations[toDisplayName];
          return newRelations;
        });
      }
    } catch (err) {
      console.error('Failed to remove relation:', err);
    } finally {
      setUpdatingRelation(null);
    }
  };

  const handleRatingChange = (toDisplayName, newRating) => {
    if (newRating === null || newRating === 0) {
      removePlayerRelation(toDisplayName);
    } else {
      setPlayerRelation(toDisplayName, newRating);
    }
  };

  const openBiographyDialog = (player) => {
    setSelectedPlayer(player);
    setBiographyDialogOpen(true);
  };

  const handleFilterModeChange = (event, newMode) => {
    if (newMode !== null) {
      setFilterMode(newMode);
    }
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getRatingLabel = (rating) => {
    switch (rating) {
      case 1: return 'Dislike';
      case 2: return 'Neutral';
      case 3: return 'Like';
      case 4: return 'Love';
      case 5: return 'Adore';
      default: return 'Unrated';
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      case 4: return 'success';
      case 5: return 'primary';
      default: return 'default';
    }
  };

  return (
    <GlassBackground>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <GlassContainer>
          <GlassPageHeader
            title="Player Relations"
            subtitle={`${totalPlayers} player${totalPlayers !== 1 ? 's' : ''} total`}
            action={
              <GlassIconButton onClick={onBack}>
                <ArrowBack />
              </GlassIconButton>
            }
          />

        {error && (
          <GlassInfoCard severity="error" sx={{ mb: 3 }}>
            {error}
          </GlassInfoCard>
        )}

        {/* Search Field with Items Per Page */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <GlassTextField
            fullWidth
            variant="outlined"
            placeholder="Search players by character name or username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{
            minWidth: 150,
            mt: 2,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(10px)',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
              },
              '&.Mui-focused': {
                background: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              },
            },
            '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
          }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value={10}>10 per page</MenuItem>
              <MenuItem value={20}>20 per page</MenuItem>
              <MenuItem value={50}>50 per page</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Filter Controls */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3
        }}>
          {debouncedSearchTerm && (
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Searching for "{debouncedSearchTerm}"
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', ml: 'auto' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Sort by:
            </Typography>
            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={handleFilterModeChange}
              size="small"
              sx={{
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                '& .MuiToggleButton-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="relationStatus">
                <FavoriteOutlined sx={{ mr: { xs: 0.5, sm: 1 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Relation Status</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Relation</Box>
              </ToggleButton>
              <ToggleButton value="recentlyActive">
                <Schedule sx={{ mr: { xs: 0.5, sm: 1 } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Recently Active</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Recent</Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {players.map((player) => (
                <Grid item xs={12} sm={6} md={4} key={player.displayName}>
                  <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, color: 'white' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CharacterAvatar
                          displayName={player.displayName}
                          profilePictureUrl={player.profilePictureUrl}
                          characterName={player.displayName}
                          isDeceased={Boolean(player.isDeceased)}
                          size={60}
                          sx={{ mr: 2 }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h2" gutterBottom sx={{ color: 'white' }}>
                            {player.displayName}
                          </Typography>
                          {player.accountOwner && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              @{player.accountOwner}
                            </Typography>
                          )}
                        </Box>
                        {player.biography && (
                          <IconButton
                            size="small"
                            onClick={() => openBiographyDialog(player)}
                            sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.8)' }}
                          >
                            <Info fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {Boolean(player.isDeceased) && (
                        <Chip
                          icon={<span style={{ fontSize: 14 }}>💀</span>}
                          label="Deceased"
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}

                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                        First seen: {formatDate(player.firstUsed)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                        Last seen: {getTimeSince(player.lastSeen)}
                      </Typography>

                      {/* Rating Section */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          Your relation:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating
                            value={relations[player.displayName] || 0}
                            onChange={(event, newValue) => handleRatingChange(player.displayName, newValue)}
                            disabled={updatingRelation === player.displayName}
                            icon={<Favorite fontSize="inherit" />}
                            emptyIcon={<FavoriteBorder fontSize="inherit" />}
                          />
                          {updatingRelation === player.displayName && (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                        {relations[player.displayName] && (
                          <Chip
                            label={getRatingLabel(relations[player.displayName])}
                            color={getRatingColor(relations[player.displayName])}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </GlassCard>
                </Grid>
              ))}
            </Grid>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}

        {!loading && players.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Person sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.6)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {debouncedSearchTerm ? 'No players found matching your search' : 'No other players found'}
            </Typography>
          </Box>
        )}

        {/* Biography Dialog */}
        <Dialog
          open={biographyDialogOpen}
          onClose={() => setBiographyDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: 'rgba(30, 30, 40, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
              color: 'white',
            }
          }}
          BackdropProps={{
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
          }}
        >
          <DialogTitle sx={{
            color: 'white',
            fontWeight: 600,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CharacterAvatar
                displayName={selectedPlayer?.displayName}
                profilePictureUrl={selectedPlayer?.profilePictureUrl}
                size={40}
              />
              <Box>
                <Typography variant="h6" sx={{ color: 'white' }}>{selectedPlayer?.displayName}</Typography>
                {selectedPlayer?.accountOwner && (
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    @{selectedPlayer.accountOwner}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
              {selectedPlayer?.biography || 'No biography available.'}
            </Typography>
          </DialogContent>
          <DialogActions sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            pt: 2,
            px: 3,
            pb: 2
          }}>
            <GlassButton variant="glass" onClick={() => setBiographyDialogOpen(false)}>Close</GlassButton>
          </DialogActions>
        </Dialog>
        </GlassContainer>
      </Container>
    </GlassBackground>
  );
};

export default PlayerRelations; 